// Возрастной порог продукта: пользоваться «Луней» можно с 14 лет.
//
// Обоснование — docs/legal.md §4.6: прямого возраста согласия в 152-ФЗ нет, практика опирается
// на ст. 26 ГК РФ (с 14 лет частичная дееспособность, до 14 за несовершеннолетнего действует
// законный представитель). Порог отдельного родительского согласия на обработку ПД не требует.
//
// Правило доменное, поэтому живёт здесь и импортируется обеими сторонами (CLAUDE.md, правило 7).
// Раньше число было записано дважды — `MIN_AGE` в user.service и `MIN_BIRTH_AGE_YEARS` в конфиге
// карточки профиля, — и совпадать им помогала только удача.
//
// Считаем по календарным частям, а не по меткам времени, и это принципиально: бэкенд хранит дату
// рождения как полночь UTC, интерфейс получает её из пикера как полночь локального времени. Общая
// арифметика поверх `Date` дала бы у них расхождение в сутки ровно на границе возраста (тот самый
// сдвиг, от которого предостерегает apps/api/src/shared/dates.ts). Поэтому каждая сторона сама
// раскладывает дату в своём поясе — `utcDateParts` на сервере, `localDateParts` в интерфейсе, —
// а формула у них одна.

/** Минимальный возраст пользователя продукта в полных годах. */
export const MIN_BIRTH_AGE_YEARS = 14

/** Календарная дата без часового пояса. `month` — 1..12, как в записи YYYY-MM-DD. */
export type DateParts = {
  year: number
  month: number
  day: number
}

/** Разложить момент по календарю UTC — так даты-only хранит и сравнивает бэкенд. */
export const utcDateParts = (date: Date): DateParts => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
})

/** Разложить момент по календарю пользователя — так даты приходят из пикера в интерфейсе. */
export const localDateParts = (date: Date): DateParts => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
})

/** Отрицательное — `a` раньше `b`, ноль — тот же календарный день. */
const compareDateParts = (a: DateParts, b: DateParts): number =>
  a.year - b.year || a.month - b.month || a.day - b.day

/**
 * Возраст в полных годах. Если день рождения в этом году ещё не наступил, год не засчитан:
 * ровно на дне рождения возраст уже новый, за день до него — ещё прежний.
 */
export const ageInYears = (birth: DateParts, today: DateParts): number => {
  const age = today.year - birth.year
  const hasNotHadBirthdayYet =
    today.month < birth.month || (today.month === birth.month && today.day < birth.day)

  return hasNotHadBirthdayYet ? age - 1 : age
}

/** Почему дата рождения не годится: `FUTURE` — дата ещё не наступила, `TOO_YOUNG` — младше 14. */
export type BirthDateRejection = 'FUTURE' | 'TOO_YOUNG'

export type BirthDateCheck = 'OK' | BirthDateRejection

/**
 * Единственная проверка даты рождения на весь продукт: по ней сервер решает, отдать ли
 * `BIRTH_DATE_IN_FUTURE`/`AGE_RESTRICTED`, а интерфейс — показать ли ошибку поля и возрастную
 * заглушку. Порог применяется к дате рождения **самого пользователя**; чужую дату (натальная
 * карта для другого, шаг совместимости) он не касается — там считается карта, а не заводится
 * учётная запись.
 */
export const checkBirthDate = (birth: DateParts, today: DateParts): BirthDateCheck => {
  if (compareDateParts(birth, today) > 0) return 'FUTURE'
  if (ageInYears(birth, today) < MIN_BIRTH_AGE_YEARS) return 'TOO_YOUNG'

  return 'OK'
}

/**
 * Самая поздняя дата рождения, при которой возраст уже дотягивает до порога. По ней барабаны
 * пикера обрезаются сверху, поэтому «младше 14» и «из будущего» руками недостижимы.
 */
export const maxAllowedBirthDateParts = (today: DateParts): DateParts => ({
  year: today.year - MIN_BIRTH_AGE_YEARS,
  month: today.month,
  day: today.day,
})
